import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Textarea,
  Select,
  Option,
  Chip,
  Sheet,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
} from '@mui/joy';
import {
  Add,
  Delete,
  Edit,
  Person,
  Visibility,
  VisibilityOff,
  People,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { contactListService } from '@/services/contactListService';
import { contactService } from '@/services/contactService';
import {
  ContactList,
  Contact,
  CreateContactListData,
  UpdateContactListData,
} from '@/types/contact';
import toast from 'react-hot-toast';

export default function ContactListsPage() {
  const { user, permissions } = useAuth();
  const [contactLists, setContactLists] = useState<ContactList[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isAddContactsModalOpen, setIsAddContactsModalOpen] = useState(false);
  const [selectedList, setSelectedList] = useState<ContactList | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);

  const canCreate = permissions.includes('contact_lists.create');
  const canEdit = permissions.includes('contact_lists.edit');
  const canDelete = permissions.includes('contact_lists.delete');

  const [formData, setFormData] = useState<CreateContactListData>({
    name: '',
    description: '',
    visibility: 'public',
  });

  useEffect(() => {
    loadContactLists();
    loadContacts();
  }, []);

  const loadContactLists = async () => {
    try {
      setIsLoading(true);
      const lists = await contactListService.getLists();
      setContactLists(lists);
    } catch (error: any) {
      toast.error('Failed to load contact lists');
      console.error('Error loading contact lists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await contactService.getContacts({ limit: 1000 });
      setContacts(response.data);
    } catch (error: any) {
      console.error('Error loading contacts:', error);
    }
  };

  const handleCreateList = () => {
    setSelectedList(null);
    setFormData({
      name: '',
      description: '',
      visibility: 'public',
    });
    setIsFormOpen(true);
  };

  const handleEditList = (list: ContactList) => {
    setSelectedList(list);
    setFormData({
      name: list.name,
      description: list.description || '',
      visibility: list.visibility,
    });
    setIsFormOpen(true);
  };

  const handleSaveList = async () => {
    try {
      if (selectedList) {
        await contactListService.updateList(selectedList.id, formData);
        toast.success('Contact list updated successfully');
      } else {
        await contactListService.createList(formData);
        toast.success('Contact list created successfully');
      }
      setIsFormOpen(false);
      loadContactLists();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save contact list');
    }
  };

  const handleDeleteList = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this contact list?')) {
      return;
    }
    try {
      await contactListService.deleteList(id);
      toast.success('Contact list deleted successfully');
      loadContactLists();
    } catch (error: any) {
      toast.error('Failed to delete contact list');
    }
  };

  const handleOpenAddContacts = async (list: ContactList) => {
    setSelectedList(list);
    setSelectedContacts([]);
    // Load list with members to get current member IDs
    try {
      const listWithMembers = await contactListService.getList(list.id, true);
      setSelectedContacts(listWithMembers.members?.map(m => m.id) || []);
    } catch (error: any) {
      console.error('Error loading list members:', error);
    }
    setIsAddContactsModalOpen(true);
  };

  const handleSaveListMembers = async () => {
    if (!selectedList) return;
    try {
      await contactListService.addMultipleContactsToList(
        selectedList.id,
        selectedContacts
      );
      toast.success('Contacts added to list successfully');
      setIsAddContactsModalOpen(false);
      loadContactLists();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to add contacts to list');
    }
  };

  const handleRemoveContactFromList = async (listId: number, contactId: number) => {
    try {
      await contactListService.removeContactFromList(listId, contactId);
      toast.success('Contact removed from list');
      loadContactLists();
    } catch (error: any) {
      toast.error('Failed to remove contact from list');
    }
  };

  const isListOwner = (list: ContactList) => {
    return user && list.created_by === user.id;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography level="h1" component="h1">
            Contact Lists
          </Typography>
          {canCreate && (
            <Button startDecorator={<Add />} onClick={handleCreateList}>
              New List
            </Button>
          )}
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : contactLists.length === 0 ? (
          <Alert color="neutral">No contact lists found</Alert>
        ) : (
          <Stack spacing={2}>
            {contactLists.map((list) => (
              <Card key={list.id}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography level="title-md">{list.name}</Typography>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={list.visibility === 'private' ? 'neutral' : 'primary'}
                          startDecorator={
                            list.visibility === 'private' ? <VisibilityOff /> : <Visibility />
                          }
                        >
                          {list.visibility === 'private' ? 'Private' : 'Public'}
                        </Chip>
                        {isListOwner(list) && (
                          <Chip size="sm" variant="outlined" color="neutral">
                            Owner
                          </Chip>
                        )}
                      </Stack>
                      {list.description && (
                        <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                          {list.description}
                        </Typography>
                      )}
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        <People sx={{ fontSize: 14, mr: 0.5 }} />
                        {list.member_count || 0} {list.member_count === 1 ? 'contact' : 'contacts'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      {canEdit && (isListOwner(list) || list.visibility === 'public') && (
                        <>
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => handleOpenAddContacts(list)}
                          >
                            <Add />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => handleEditList(list)}
                          >
                            <Edit />
                          </IconButton>
                        </>
                      )}
                      {canDelete && (isListOwner(list) || list.visibility === 'public') && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleDeleteList(list.id)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>

                  {list.members && list.members.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Members:
                      </Typography>
                      <List size="sm">
                        {list.members.map((contact) => (
                          <ListItem key={contact.id}>
                            <ListItemDecorator>
                              <Person />
                            </ListItemDecorator>
                            <ListItemContent>
                              <Typography level="body-sm">
                                {contact.first_name} {contact.last_name}
                                {contact.company && ` - ${contact.company}`}
                              </Typography>
                            </ListItemContent>
                            {canEdit && (isListOwner(list) || list.visibility === 'public') && (
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                onClick={() => handleRemoveContactFromList(list.id, contact.id)}
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Box>

      {/* Contact List Form Modal */}
      <Modal open={isFormOpen} onClose={() => setIsFormOpen(false)}>
        <ModalDialog sx={{ maxWidth: 500 }}>
          <ModalClose />
          <Typography level="h2" sx={{ mb: 2 }}>
            {selectedList ? 'Edit Contact List' : 'New Contact List'}
          </Typography>
          <Stack spacing={2}>
            <FormControl required>
              <Input
                placeholder="List Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <Textarea
                placeholder="Description"
                minRows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <Select
                value={formData.visibility}
                onChange={(_, value) =>
                  setFormData({ ...formData, visibility: value as 'public' | 'private' })
                }
              >
                <Option value="public">Public - Visible to all users</Option>
                <Option value="private">Private - Only visible to you</Option>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <Button onClick={handleSaveList}>Save</Button>
              <Button variant="outlined" onClick={() => setIsFormOpen(false)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Add Contacts to List Modal */}
      <Modal open={isAddContactsModalOpen} onClose={() => setIsAddContactsModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h2" sx={{ mb: 2 }}>
            Add Contacts to {selectedList?.name}
          </Typography>
          <Stack spacing={2}>
            <Sheet sx={{ maxHeight: 400, overflow: 'auto' }}>
              <List>
                {contacts.map((contact) => (
                  <ListItem key={contact.id}>
                    <ListItemDecorator>
                      <Person />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">
                        {contact.first_name} {contact.last_name}
                        {contact.company && ` - ${contact.company}`}
                      </Typography>
                    </ListItemContent>
                    <IconButton
                      size="sm"
                      variant={selectedContacts.includes(contact.id) ? 'solid' : 'outlined'}
                      color={selectedContacts.includes(contact.id) ? 'primary' : 'neutral'}
                      onClick={() => {
                        if (selectedContacts.includes(contact.id)) {
                          setSelectedContacts(selectedContacts.filter(id => id !== contact.id));
                        } else {
                          setSelectedContacts([...selectedContacts, contact.id]);
                        }
                      }}
                    >
                      {selectedContacts.includes(contact.id) ? <Delete /> : <Add />}
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Sheet>
            <Stack direction="row" spacing={2}>
              <Button onClick={handleSaveListMembers}>
                Add {selectedContacts.length} {selectedContacts.length === 1 ? 'Contact' : 'Contacts'}
              </Button>
              <Button variant="outlined" onClick={() => setIsAddContactsModalOpen(false)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Container>
  );
}

